
import React from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { images } from '@/assets/images';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { FileText, Shield, Info, ArrowRight, Clock, Users, Scale, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '@/components/seo/SEOHead';

const LegalHub = () => {
  const legalDocuments = [
    {
      id: 'terms',
      title: 'Terms of Service',
      description: 'Usage terms, conditions, and legal agreements',
      icon: FileText,
      path: '/terms-of-service',
      gradient: 'from-cyan-500 to-blue-600',
      readTime: '8 min read',
      category: 'Legal Agreement'
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      description: 'How we protect and handle your personal data',
      icon: Shield,
      path: '/privacy',
      gradient: 'from-green-500 to-emerald-600',
      readTime: '12 min read',
      category: 'Data Protection'
    },
    {
      id: 'disclaimers',
      title: 'Financial Disclaimers',
      description: 'Important notices about our financial calculators',
      icon: Info,
      path: '/disclaimers',
      gradient: 'from-amber-500 to-orange-600',
      readTime: '6 min read',
      category: 'Financial Notice'
    },
    {
      id: 'eula',
      title: 'End User License Agreement',
      description: 'Software licensing terms, AI consent, and usage restrictions',
      icon: Lock,
      path: '/eula',
      gradient: 'from-purple-500 to-indigo-600',
      readTime: '25 min read',
      category: 'License Agreement'
    },
    {
      id: 'compliance',
      title: 'Compliance & Security',
      description: 'SOC 2, ISO 27001, ISO 42001, PCI-DSS, GDPR compliance details',
      icon: Shield,
      path: '/compliance',
      gradient: 'from-rose-500 to-pink-600',
      readTime: '10 min read',
      category: 'Security'
    }
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <SEOHead
          title="Legal Center — Privacy, Terms & Policies | EquiForge"
          description="Review EquiForge's legal documentation including Privacy Policy, Terms of Service, EULA, compliance certifications, and financial disclaimers."
          keywords="privacy policy, terms of service, EULA, legal center, GDPR, data protection, EquiForge legal"
          canonicalPath="/legal-hub"
        />
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10" />
          
          <Link to="/dashboard" className="absolute top-4 left-4 z-50">
            <img
              src={images.equiforgeLogoFlat}
              alt="EquiForge"
              className="h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md hover:scale-105 transition-transform duration-300"
            />
          </Link>
          
          <div className="container mx-auto px-4 py-16 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <Scale className="w-12 h-12 text-cyan-400 mr-4" />
                <h1 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent font-orbitron">
                  Legal & Privacy Center
                </h1>
              </div>
              
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Transparency and trust are at the core of EquiForge™. Review our comprehensive legal documentation 
                to understand how we protect your data and provide our services.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                <div className="text-center">
                  <Users className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">GDPR Compliant</p>
                </div>
                <div className="text-center">
                  <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">SOC 2 Type II</p>
                </div>
                <div className="text-center">
                  <Scale className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Legal Compliance</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Documents Grid */}
        <div className="container mx-auto px-4 pb-16">
          <div className="grid lg:grid-cols-3 gap-8">
            {legalDocuments.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={doc.path} className="block group">
                  <Card className="h-full bg-slate-800/30 border-slate-700/50 backdrop-blur-sm hover:bg-slate-700/30 transition-all duration-300 group-hover:border-cyan-500/50 group-hover:shadow-xl group-hover:shadow-cyan-500/10">
                    <CardContent className="p-8">
                      {/* Icon & Category */}
                      <div className="flex items-center justify-between mb-6">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${doc.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <doc.icon className="w-8 h-8 text-white" />
                        </div>
                        <span className="text-xs text-slate-400 bg-slate-900/50 px-3 py-1 rounded-full">
                          {doc.category}
                        </span>
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-bold text-slate-200 mb-3 group-hover:text-cyan-400 transition-colors duration-300 font-orbitron">
                        {doc.title}
                      </h3>
                      
                      <p className="text-slate-400 mb-6 leading-relaxed">
                        {doc.description}
                      </p>

                      {/* Meta Info */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-slate-500">
                          <Clock className="w-4 h-4 mr-2" />
                          {doc.readTime}
                        </div>
                        
                        <div className="flex items-center text-cyan-400 group-hover:text-cyan-300 transition-colors duration-300">
                          <span className="text-sm font-medium mr-2">Read More</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Quick Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16"
          >
            <Card className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border-slate-600/50 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-200 mb-3 font-orbitron">
                      Need Legal Assistance?
                    </h3>
                    <p className="text-slate-400 mb-4">
                      Our legal team is available to answer questions about our policies, 
                      data handling practices, or any compliance concerns.
                    </p>
                    <div className="space-y-2 text-sm text-slate-400">
                      <p>📧 legal@equiforge.com</p>
                      <p>📧 privacy@equiforge.com</p>
                      <p>⏰ Response within 48 hours</p>
                    </div>
                  </div>
                  
                  <div className="text-center md:text-right">
                    <Button className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white px-8 py-3 text-lg">
                      Contact Legal Team
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </AppLayout>
  );
};

export default LegalHub;
